import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { maintenanceApi, devicesApi, usersApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import {
  Wrench,
  Plus,
  Calendar,
  AlertTriangle,
  Clock,
  DollarSign,
  Gauge,
  X,
  Loader2,
  Droplets,
  Filter,
  Circle,
  Octagon,
  Cog,
  Battery,
  Thermometer,
  ClipboardCheck,
  Fuel,
  Pencil,
  Trash2,
  Users,
  Check,
  Repeat,
  FileText,
  FileSpreadsheet,
  Eye,
  Upload,
  Paperclip,
} from 'lucide-react';

interface MaintenanceType {
  id: string;
  name: string;
  icon: string;
  color: string;
  intervalKm: number | null;
  intervalMonths: number | null;
  estimatedCost: number | null;
}

interface MaintenanceRecord {
  id: string;
  deviceId: string;
  type: { id: string; name: string; icon: string; color: string };
  performedAt: string;
  performedBy: string | null;
  odometerKm: number;
  totalCost: number | null;
  currency: string;
  description: string | null;
  invoiceUrl: string | null;
  /** 'fuel' rows come from the FUEL module — read-only here, edit in /fuel. */
  source?: 'maintenance' | 'fuel';
}

type RecurrenceType = 'MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL' | 'TWO_YEARS' | 'FOUR_YEARS';

interface MaintenanceSchedule {
  id: string;
  deviceId: string;
  type: { id: string; name: string; icon: string; color: string };
  status: string;
  priority: string;
  dueDate: string | null;
  dueOdometerKm: number | null;
  estimatedCost: number | null;
  currency: string;
  notes: string | null;
  notifyUserIds: string[];
  recurrence: RecurrenceType | null;
}

interface TenantUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface Device {
  id: string;
  name: string;
  plate: string | null;
}

// Les fichiers de facture sont stockés sous la forme `<uuid>-<originalname>`
// (cf. multer dans maintenance.routes.ts). On retire le préfixe UUID pour
// afficher uniquement le nom original.
const UUID_PREFIX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;
const getOriginalFilename = (url: string): string => {
  const filename = url.split('/').pop() ?? url;
  return filename.replace(UUID_PREFIX_RE, '');
};

const iconMap: Record<string, React.ElementType> = {
  wrench: Wrench,
  droplets: Droplets,
  filter: Filter,
  circle: Circle,
  octagon: Octagon,
  cog: Cog,
  battery: Battery,
  thermometer: Thermometer,
  'clipboard-check': ClipboardCheck,
  fuel: Fuel,
};

export default function MaintenancePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [activeTab, setActiveTab] = useState<'schedules' | 'history'>('schedules');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'record' | 'schedule'>('record');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<MaintenanceSchedule | null>(null);
  const [completingSchedule, setCompletingSchedule] = useState<MaintenanceSchedule | null>(null);
  type PeriodPreset =
    | 'all'
    | '7d'
    | '30d'
    | '90d'
    | '1y'
    | 'current_month'
    | 'previous_month'
    | 'current_year'
    | 'previous_year'
    | 'custom';
  // Par défaut, on borne sur les 30 derniers jours pour limiter l'historique
  // affiché ; l'utilisateur peut élargir via le filtre période.
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('30d');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [isExporting, setIsExporting] = useState<'xlsx' | 'pdf' | null>(null);

  // Format Date → "YYYY-MM-DD" en tenant compte du fuseau local.
  const toInputDate = (d: Date) => {
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };

  // Recalcule les champs date à chaque changement de preset (sauf 'custom').
  useEffect(() => {
    if (periodPreset === 'custom') return;
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = now;
    switch (periodPreset) {
      case 'all':
        start = null;
        end = null;
        break;
      case '7d': start = new Date(now); start.setDate(start.getDate() - 7); break;
      case '30d': start = new Date(now); start.setDate(start.getDate() - 30); break;
      case '90d': start = new Date(now); start.setDate(start.getDate() - 90); break;
      case '1y': start = new Date(now); start.setFullYear(start.getFullYear() - 1); break;
      case 'current_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'previous_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'current_year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      case 'previous_year':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
    }
    setCustomStartDate(start ? toInputDate(start) : '');
    setCustomEndDate(end ? toInputDate(end) : '');
  }, [periodPreset]);

  // L'API utilise toujours les champs date affichés à l'écran.
  const dateRange = {
    startDate: customStartDate ? new Date(`${customStartDate}T00:00:00`).toISOString() : undefined,
    endDate: customEndDate ? new Date(`${customEndDate}T23:59:59`).toISOString() : undefined,
  };

  // Fetch data
  const { data: statsData } = useQuery({
    queryKey: ['maintenance-stats', selectedDeviceId, tenantKey],
    queryFn: async () => {
      const res = await maintenanceApi.getStats(selectedDeviceId || undefined);
      return res.data;
    },
  });

  const { data: typesData } = useQuery({
    queryKey: ['maintenance-types', tenantKey],
    queryFn: async () => {
      const res = await maintenanceApi.getTypes();
      return res.data;
    },
  });

  const { data: schedulesData, isLoading: schedulesLoading } = useQuery({
    queryKey: ['maintenance-schedules', selectedDeviceId, tenantKey],
    queryFn: async () => {
      const res = await maintenanceApi.getSchedules({
        deviceId: selectedDeviceId || undefined,
        limit: 50,
      });
      return res.data;
    },
  });

  const { data: recordsData, isLoading: recordsLoading } = useQuery({
    queryKey: ['maintenance-records', selectedDeviceId, selectedTypeId, periodPreset, customStartDate, customEndDate, tenantKey],
    queryFn: async () => {
      const res = await maintenanceApi.getRecords({
        deviceId: selectedDeviceId || undefined,
        typeId: selectedTypeId || undefined,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: 100,
      });
      return res.data;
    },
  });

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    setIsExporting(format);
    try {
      const res = await maintenanceApi.exportRecords({
        format,
        deviceId: selectedDeviceId || undefined,
        typeId: selectedTypeId || undefined,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      const blob = new Blob([res.data], {
        type:
          format === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `maintenance_${today}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setIsExporting(null);
    }
  };

  const { data: devicesData } = useQuery({
    queryKey: ['devices-list', tenantKey],
    queryFn: async () => {
      const res = await devicesApi.list({ limit: 1000 });
      return res.data;
    },
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-list', tenantKey],
    queryFn: async () => {
      const res = await usersApi.list({ limit: 200 });
      return res.data;
    },
  });

  // Init default types if none exist
  const initTypesMutation = useMutation({
    mutationFn: () => maintenanceApi.initDefaultTypes(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-types'] });
    },
  });

  const deleteRecordMutation = useMutation({
    mutationFn: (id: string) => maintenanceApi.deleteRecord(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-records'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: (id: string) => maintenanceApi.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
    },
  });


  useEffect(() => {
    if (typesData?.data?.length === 0) {
      initTypesMutation.mutate();
    }
  }, [typesData]);

  const types: MaintenanceType[] = typesData?.data || [];
  const allSchedules: MaintenanceSchedule[] = schedulesData?.data || [];
  const schedules = allSchedules.filter((s) => s.status !== 'COMPLETED' && s.status !== 'CANCELLED');
  const records: MaintenanceRecord[] = recordsData?.data || [];
  const devices: Device[] = devicesData?.data || [];
  const tenantUsers: TenantUser[] = usersData?.data || [];

  const stats = statsData || {
    totalRecords: 0,
    totalCost: 0,
    scheduledCount: 0,
    overdueCount: 0,
  };

  const getDeviceName = (deviceId: string) => {
    const device = devices.find((d) => d.id === deviceId);
    return device ? device.name : t('maintenance.vehicle_unknown');
  };

  const fmtCurrencyTotal = (totals: Record<string, number>) =>
    Object.entries(totals)
      .map(([cur, sum]) => `${sum.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`)
      .join(' + ') || '—';

  const groupedRecords = useMemo(() => {
    // Tri: par véhicule (alphabétique), puis date desc dans chaque groupe.
    const sorted = [...records].sort((a, b) => {
      const nA = getDeviceName(a.deviceId);
      const nB = getDeviceName(b.deviceId);
      if (nA !== nB) return nA.localeCompare(nB, 'fr');
      return new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime();
    });
    const groups: Array<{
      deviceId: string;
      deviceName: string;
      records: MaintenanceRecord[];
      subtotal: Record<string, number>;
    }> = [];
    for (const r of sorted) {
      let group = groups[groups.length - 1];
      if (!group || group.deviceId !== r.deviceId) {
        group = { deviceId: r.deviceId, deviceName: getDeviceName(r.deviceId), records: [], subtotal: {} };
        groups.push(group);
      }
      group.records.push(r);
      if (r.totalCost != null) {
        group.subtotal[r.currency] = (group.subtotal[r.currency] ?? 0) + r.totalCost;
      }
    }
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, devices]);

  const grandTotal = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const r of records) {
      if (r.totalCost != null) {
        totals[r.currency] = (totals[r.currency] ?? 0) + r.totalCost;
      }
    }
    return totals;
  }, [records]);

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Wrench;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'HIGH':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'MEDIUM':
        return 'bg-primary-500/20 text-primary-400 border-primary-500/30';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OVERDUE':
        return 'bg-red-500/20 text-red-400';
      case 'SCHEDULED':
        return 'bg-primary-500/20 text-primary-400';
      case 'IN_PROGRESS':
        return 'bg-amber-500/20 text-amber-400';
      case 'COMPLETED':
        return 'bg-emerald-500/20 text-emerald-400';
      default:
        return 'bg-gray-100 text-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('maintenance.title')}</h1>
          <p className="text-gray-500 mt-1">{t('maintenance.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setModalMode('record');
              setIsAddModalOpen(true);
            }}
            className="btn-secondary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('maintenance.add')}
          </button>
          <button
            onClick={() => {
              setModalMode('schedule');
              setIsAddModalOpen(true);
            }}
            className="btn-glow flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            {t('maintenance.schedule')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card stat-card-blue">
          <div className="flex items-start justify-between">
            <div className="p-2.5 rounded-xl bg-primary-500/10">
              <Wrench className="h-5 w-5 text-primary-400" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-gray-900">{stats.totalRecords}</p>
            <p className="text-sm text-gray-500 mt-1">{t('maintenance.stat_records')}</p>
          </div>
        </div>

        <div className="stat-card stat-card-green">
          <div className="flex items-start justify-between">
            <div className="p-2.5 rounded-xl bg-emerald-500/10">
              <DollarSign className="h-5 w-5 text-emerald-400" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-gray-900">
              {stats.totalCost.toLocaleString('fr-FR')} EUR
            </p>
            <p className="text-sm text-gray-500 mt-1">{t('maintenance.stat_total_cost')}</p>
          </div>
        </div>

        <div className="stat-card stat-card-purple">
          <div className="flex items-start justify-between">
            <div className="p-2.5 rounded-xl bg-accent-purple/10">
              <Clock className="h-5 w-5 text-accent-purple" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-gray-900">{stats.scheduledCount}</p>
            <p className="text-sm text-gray-500 mt-1">{t('maintenance.stat_scheduled')}</p>
          </div>
        </div>

        <div className="stat-card stat-card-orange">
          <div className="flex items-start justify-between">
            <div className="p-2.5 rounded-xl bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-gray-900">{stats.overdueCount}</p>
            <p className="text-sm text-gray-500 mt-1">{t('maintenance.stat_overdue')}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div>
            <label className="block text-sm text-gray-500 mb-2">{t('maintenance.filter_vehicle')}</label>
            <select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              className="select-dark w-full sm:w-64"
            >
              <option value="">{t('maintenance.all_vehicles')}</option>
              {devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name} {device.plate && `(${device.plate})`}
                </option>
              ))}
            </select>
          </div>
          {activeTab === 'history' && (
            <>
              <div>
                <label className="block text-sm text-gray-500 mb-2">{t('maintenance.filter_type')}</label>
                <select
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                  className="select-dark w-full sm:w-56"
                >
                  <option value="">{t('maintenance.all_types')}</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-2">{t('maintenance.filter_period')}</label>
                <select
                  value={periodPreset}
                  onChange={(e) => setPeriodPreset(e.target.value as PeriodPreset)}
                  className="select-dark w-full sm:w-56"
                >
                  <option value="all">{t('maintenance.period_all')}</option>
                  <option value="7d">{t('maintenance.period_7d')}</option>
                  <option value="30d">{t('maintenance.period_30d')}</option>
                  <option value="90d">{t('maintenance.period_90d')}</option>
                  <option value="1y">{t('maintenance.period_1y')}</option>
                  <option value="current_month">{t('maintenance.period_current_month')}</option>
                  <option value="previous_month">{t('maintenance.period_previous_month')}</option>
                  <option value="current_year">{t('maintenance.period_current_year')}</option>
                  <option value="previous_year">{t('maintenance.period_previous_year')}</option>
                  <option value="custom">{t('maintenance.period_custom')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-2">{t('maintenance.period_from')}</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => {
                    setCustomStartDate(e.target.value);
                    setPeriodPreset('custom');
                  }}
                  className="select-dark w-full sm:w-48"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-500 mb-2">{t('maintenance.period_to')}</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => {
                    setCustomEndDate(e.target.value);
                    setPeriodPreset('custom');
                  }}
                  className="select-dark w-full sm:w-48"
                />
              </div>
            </>
          )}
          {activeTab === 'history' && (
            <div className="flex gap-2 lg:ml-auto">
              <button
                type="button"
                onClick={() => handleExport('xlsx')}
                disabled={isExporting !== null}
                className="btn-secondary flex items-center gap-2"
                title={t('maintenance.export_xlsx_title')}
              >
                {isExporting === 'xlsx' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" />
                )}
                <span>{t('maintenance.export_xlsx')}</span>
              </button>
              <button
                type="button"
                onClick={() => handleExport('pdf')}
                disabled={isExporting !== null}
                className="btn-secondary flex items-center gap-2"
                title={t('maintenance.export_pdf_title')}
              >
                {isExporting === 'pdf' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                <span>{t('maintenance.export_pdf')}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-list inline-flex">
        <button
          onClick={() => setActiveTab('schedules')}
          className={`tab-item ${activeTab === 'schedules' ? 'tab-item-active' : ''}`}
        >
          {t('maintenance.tab_schedules', { count: schedules.length })}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`tab-item ${activeTab === 'history' ? 'tab-item-active' : ''}`}
        >
          {t('maintenance.tab_history', { count: records.length })}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'schedules' && (
        <div className="glass-card overflow-hidden">
          {schedulesLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t('maintenance.no_scheduled')}</p>
              <button
                onClick={() => {
                  setModalMode('schedule');
                  setIsAddModalOpen(true);
                }}
                className="btn-glow mt-4"
              >
                {t('maintenance.schedule_one')}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {schedules.map((schedule) => {
                const Icon = getIcon(schedule.type.icon);
                return (
                  <div
                    key={schedule.id}
                    className="p-4 hover:bg-white/5 transition-colors flex items-center gap-4"
                  >
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${schedule.type.color}20` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: schedule.type.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{schedule.type.name}</p>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(
                            schedule.priority
                          )}`}
                        >
                          {schedule.priority}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            schedule.status
                          )}`}
                        >
                          {schedule.status === 'OVERDUE' ? t('maintenance.status_overdue') : t('maintenance.status_scheduled')}
                        </span>
                        {schedule.recurrence && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/20 text-violet-400 flex items-center gap-1">
                            <Repeat className="h-3 w-3" />
                            {{
                              MONTHLY: t('maintenance.recurrence_monthly'),
                              QUARTERLY: t('maintenance.recurrence_quarterly'),
                              SEMI_ANNUAL: t('maintenance.recurrence_semi_annual'),
                              ANNUAL: t('maintenance.recurrence_annual'),
                              TWO_YEARS: t('maintenance.recurrence_2y'),
                              FOUR_YEARS: t('maintenance.recurrence_4y'),
                            }[schedule.recurrence]}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {getDeviceName(schedule.deviceId)}
                      </p>
                    </div>
                    <div className="text-right">
                      {schedule.dueDate && (
                        <p className="text-sm text-gray-900">
                          {new Date(schedule.dueDate).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                      {schedule.dueOdometerKm && (
                        <p className="text-xs text-gray-500 flex items-center justify-end gap-1">
                          <Gauge className="h-3 w-3" />
                          {schedule.dueOdometerKm.toLocaleString()} km
                        </p>
                      )}
                      {schedule.estimatedCost && (
                        <p className="text-xs text-gray-500">
                          ~{schedule.estimatedCost} {schedule.currency}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setCompletingSchedule(schedule);
                          setModalMode('record');
                          setIsAddModalOpen(true);
                        }}
                        className="p-2 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                        title={t('maintenance.validate_done')}
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingSchedule(schedule);
                          setModalMode('schedule');
                          setIsAddModalOpen(true);
                        }}
                        className="p-2 rounded-lg text-gray-400 hover:text-primary-500 hover:bg-primary-50 transition-colors"
                        title={t('maintenance.edit')}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t('maintenance.delete_schedule_confirm'))) {
                            deleteScheduleMutation.mutate(schedule.id);
                          }
                        }}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title={t('maintenance.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="glass-card overflow-hidden">
          {recordsLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
            </div>
          ) : records.length === 0 ? (
            <div className="p-8 text-center">
              <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t('maintenance.no_history')}</p>
              <button
                onClick={() => {
                  setModalMode('record');
                  setIsAddModalOpen(true);
                }}
                className="btn-glow mt-4"
              >
                {t('maintenance.add_one')}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">{t('maintenance.col_type')}</th>
                    <th className="px-4 py-3 font-medium">{t('maintenance.col_vehicle')}</th>
                    <th className="px-4 py-3 font-medium">{t('maintenance.col_description')}</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap w-px">{t('maintenance.col_date')}</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap w-px text-right">{t('maintenance.col_odometer')}</th>
                    <th className="px-4 py-3 font-medium whitespace-nowrap w-px">{t('maintenance.col_performed_by')}</th>
                    <th className="px-4 py-3 font-medium text-right whitespace-nowrap w-px">{t('maintenance.col_cost')}</th>
                    <th className="px-4 py-3 font-medium text-right whitespace-nowrap w-px">{t('maintenance.col_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groupedRecords.map((group) => (
                    <Fragment key={group.deviceId}>
                      <tr className="bg-primary-50/70">
                        <td
                          colSpan={8}
                          className="px-4 py-2 text-sm font-semibold text-primary-900 uppercase tracking-wide"
                        >
                          {group.deviceName}
                          <span className="ml-2 text-xs font-normal text-primary-700/80">
                            ({group.records.length})
                          </span>
                        </td>
                      </tr>
                      {group.records.map((record) => {
                        const Icon = getIcon(record.type.icon);
                        return (
                          <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div
                                  className="p-2 rounded-lg flex-shrink-0"
                                  style={{ backgroundColor: `${record.type.color}20` }}
                                >
                                  <Icon className="h-4 w-4" style={{ color: record.type.color }} />
                                </div>
                                <span className="font-medium text-gray-900">{record.type.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                              {getDeviceName(record.deviceId)}
                            </td>
                            <td className="px-4 py-3 text-gray-600 max-w-xs">
                              <span className="block truncate" title={record.description || ''}>
                                {record.description || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                              {new Date(record.performedAt).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                              {record.odometerKm.toLocaleString('de-DE')} km
                            </td>
                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                              {record.performedBy || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              {record.totalCost != null ? (
                                <span className="font-medium text-emerald-600">
                                  {record.totalCost.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {record.currency}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1">
                                {record.source === 'fuel' ? (
                                  <Link
                                    to="/fuel"
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                                    title="Géré dans le module Carburant"
                                  >
                                    <Fuel className="h-3 w-3" />
                                    Carburant
                                  </Link>
                                ) : (
                                  <>
                                    {record.invoiceUrl && (
                                      <a
                                        href={record.invoiceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 rounded-lg text-gray-400 hover:text-primary-500 hover:bg-primary-50 transition-colors"
                                        title={t('maintenance.invoice_view')}
                                      >
                                        <Eye className="h-4 w-4" />
                                      </a>
                                    )}
                                    <button
                                      onClick={() => {
                                        setEditingRecord(record);
                                        setModalMode('record');
                                        setIsAddModalOpen(true);
                                      }}
                                      className="p-2 rounded-lg text-gray-400 hover:text-primary-500 hover:bg-primary-50 transition-colors"
                                      title={t('maintenance.edit')}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (confirm(t('maintenance.delete_record_confirm'))) {
                                          deleteRecordMutation.mutate(record.id);
                                        }
                                      }}
                                      className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                      title={t('maintenance.delete')}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td colSpan={6} className="px-4 py-2 text-right text-sm font-bold text-gray-700 whitespace-nowrap">
                          {t('maintenance.subtotal_label', { vehicle: group.deviceName })}
                        </td>
                        <td className="px-4 py-2 text-right text-sm font-bold text-emerald-700 whitespace-nowrap">
                          {fmtCurrencyTotal(group.subtotal)}
                        </td>
                        <td />
                      </tr>
                    </Fragment>
                  ))}
                  <tr className="bg-primary-100 border-t-2 border-primary-300">
                    <td colSpan={6} className="px-4 py-3 text-right text-sm font-bold text-primary-900 uppercase tracking-wide whitespace-nowrap">
                      {t('maintenance.total_global')}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-primary-900 whitespace-nowrap">
                      {fmtCurrencyTotal(grandTotal)}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isAddModalOpen && (
        <AddMaintenanceModal
          mode={modalMode}
          types={types}
          devices={devices}
          tenantUsers={tenantUsers}
          editingRecord={editingRecord}
          editingSchedule={editingSchedule}
          completingSchedule={completingSchedule}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingRecord(null);
            setEditingSchedule(null);
            setCompletingSchedule(null);
          }}
          onSuccess={() => {
            setIsAddModalOpen(false);
            setEditingRecord(null);
            setEditingSchedule(null);
            setCompletingSchedule(null);
            queryClient.invalidateQueries({ queryKey: ['maintenance-records'] });
            queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
            queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
          }}
        />
      )}
    </div>
  );
}

function AddMaintenanceModal({
  mode,
  types,
  devices,
  tenantUsers,
  editingRecord,
  editingSchedule,
  completingSchedule,
  onClose,
  onSuccess,
}: {
  mode: 'record' | 'schedule';
  types: MaintenanceType[];
  devices: Device[];
  tenantUsers: TenantUser[];
  editingRecord: MaintenanceRecord | null;
  editingSchedule: MaintenanceSchedule | null;
  completingSchedule: MaintenanceSchedule | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const isEditing = mode === 'record' ? !!editingRecord : !!editingSchedule;
  const isCompleting = !!completingSchedule;

  const [deviceId, setDeviceId] = useState(
    completingSchedule?.deviceId || (mode === 'record' ? (editingRecord?.deviceId || '') : (editingSchedule?.deviceId || ''))
  );
  const [typeId, setTypeId] = useState(
    completingSchedule?.type.id || (mode === 'record' ? (editingRecord?.type.id || '') : (editingSchedule?.type.id || ''))
  );
  const [performedAt, setPerformedAt] = useState(() => {
    if (editingRecord) {
      // Convert UTC to local time for the datetime-local input
      const d = new Date(editingRecord.performedAt);
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [odometerKm, setOdometerKm] = useState(
    editingRecord ? String(editingRecord.odometerKm) : ''
  );
  const [performedBy, setPerformedBy] = useState(editingRecord?.performedBy || '');
  const [totalCost, setTotalCost] = useState(
    editingRecord?.totalCost ? String(editingRecord.totalCost) : (completingSchedule?.estimatedCost ? String(completingSchedule.estimatedCost) : '')
  );
  const [description, setDescription] = useState(
    mode === 'record' ? (editingRecord?.description || '') : (editingSchedule?.notes || '')
  );
  const [dueDate, setDueDate] = useState(() => {
    if (editingSchedule?.dueDate) {
      // Convert UTC to local time for the datetime-local input
      const d = new Date(editingSchedule.dueDate);
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
    return '';
  });
  const [dueOdometerKm, setDueOdometerKm] = useState(
    editingSchedule?.dueOdometerKm ? String(editingSchedule.dueOdometerKm) : ''
  );
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'>(
    (editingSchedule?.priority as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT') || 'MEDIUM'
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    editingSchedule?.notifyUserIds || []
  );
  const [recurrence, setRecurrence] = useState<RecurrenceType | ''>(
    editingSchedule?.recurrence || ''
  );
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(editingRecord?.invoiceUrl || null);
  const [invoiceTouched, setInvoiceTouched] = useState(false);
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  const handleInvoiceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setIsUploadingInvoice(true);
    try {
      const res = await maintenanceApi.uploadInvoice(file);
      setInvoiceUrl(res.data.url);
      setInvoiceTouched(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setUploadError(error.response?.data?.error || t('maintenance.invoice_upload_error'));
    } finally {
      setIsUploadingInvoice(false);
      e.target.value = '';
    }
  };

  const handleInvoiceRemove = async () => {
    // Si le fichier vient juste d'être uploadé dans cette session, on le supprime du disque
    // (best-effort — si ça échoue, on continue, le nettoyage périodique s'en chargera).
    const isFreshUpload = invoiceTouched && invoiceUrl && invoiceUrl !== editingRecord?.invoiceUrl;
    if (isFreshUpload && invoiceUrl) {
      const filename = invoiceUrl.split('/').pop();
      if (filename) {
        try {
          await maintenanceApi.deleteInvoice(filename);
        } catch {
          // ignore — on remet juste à null côté UI
        }
      }
    }
    setInvoiceUrl(null);
    setInvoiceTouched(true);
  };

  useEffect(() => {
    if (!showUserDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (mode === 'record') {
        if (editingRecord) {
          await maintenanceApi.updateRecord(editingRecord.id, {
            performedAt: new Date(performedAt).toISOString(),
            odometerKm: parseFloat(odometerKm),
            performedBy: performedBy || undefined,
            totalCost: totalCost ? parseFloat(totalCost) : undefined,
            description: description || undefined,
            // Si l'utilisateur a explicitement retiré le fichier, on envoie null
            // pour forcer la mise à NULL côté Prisma. Sinon, on n'envoie pas le champ.
            ...(invoiceTouched ? { invoiceUrl: invoiceUrl } : {}),
          });
        } else {
          await maintenanceApi.createRecord({
            deviceId,
            typeId,
            performedAt: new Date(performedAt).toISOString(),
            odometerKm: parseFloat(odometerKm),
            performedBy: performedBy || undefined,
            totalCost: totalCost ? parseFloat(totalCost) : undefined,
            description: description || undefined,
            invoiceUrl: invoiceUrl || undefined,
            ...(completingSchedule ? { scheduleId: completingSchedule.id } : {}),
          });
        }
      } else {
        if (editingSchedule) {
          await maintenanceApi.updateSchedule(editingSchedule.id, {
            dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
            dueOdometerKm: dueOdometerKm ? parseFloat(dueOdometerKm) : undefined,
            priority,
            notes: description || undefined,
            notifyUserIds: selectedUserIds,
            recurrence: recurrence || null,
          });
        } else {
          await maintenanceApi.createSchedule({
            deviceId,
            typeId,
            dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
            dueOdometerKm: dueOdometerKm ? parseFloat(dueOdometerKm) : undefined,
            priority,
            notes: description || undefined,
            notifyUserIds: selectedUserIds,
            recurrence: recurrence || null,
          });
        }
      }
      onSuccess();
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: string; details?: Array<{ field: string; message: string }> } };
      };
      const detail = error.response?.data?.details?.[0];
      const msg = detail
        ? `${error.response?.data?.error ?? ''} — ${detail.field}: ${detail.message}`
        : error.response?.data?.error;
      setError(msg || t('maintenance.error_save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">
            {isCompleting
              ? t('maintenance.modal_validate')
              : isEditing
                ? (mode === 'record' ? t('maintenance.modal_edit_record') : t('maintenance.modal_edit_schedule'))
                : (mode === 'record' ? t('maintenance.modal_add_record') : t('maintenance.modal_add_schedule'))
            }
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_vehicle')}</label>
            <select
              required
              disabled={isCompleting}
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              className={`select-dark ${isCompleting ? 'opacity-60' : ''}`}
            >
              <option value="">{t('maintenance.field_vehicle_select')}</option>
              {devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name} {device.plate && `(${device.plate})`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_type')}</label>
            <select
              required
              disabled={isCompleting}
              value={typeId}
              onChange={(e) => setTypeId(e.target.value)}
              className={`select-dark ${isCompleting ? 'opacity-60' : ''}`}
            >
              <option value="">{t('maintenance.field_type_select')}</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {mode === 'record' ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_performed_at')}</label>
                  <input
                    type="datetime-local"
                    required
                    value={performedAt}
                    onChange={(e) => setPerformedAt(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_odometer')}</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="1"
                    placeholder="125000"
                    value={odometerKm}
                    onChange={(e) => setOdometerKm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_performed_by')}</label>
                  <input
                    type="text"
                    placeholder={t('maintenance.field_performed_by_placeholder')}
                    value={performedBy}
                    onChange={(e) => setPerformedBy(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_total_cost')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="150.00"
                    value={totalCost}
                    onChange={(e) => setTotalCost(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_invoice')}</label>
                {invoiceUrl ? (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                    <Paperclip className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    <a
                      href={invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-sm text-primary-600 hover:underline truncate"
                      title={getOriginalFilename(invoiceUrl)}
                    >
                      {getOriginalFilename(invoiceUrl)}
                    </a>
                    <button
                      type="button"
                      onClick={handleInvoiceRemove}
                      className="p-1 rounded text-gray-400 hover:text-red-500"
                      title={t('maintenance.invoice_remove')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 border-dashed rounded-lg text-sm text-gray-600 cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                    {isUploadingInvoice ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    <span>{isUploadingInvoice ? t('maintenance.invoice_uploading') : t('maintenance.invoice_upload_hint')}</span>
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={handleInvoiceUpload}
                      disabled={isUploadingInvoice}
                      className="hidden"
                    />
                  </label>
                )}
                {uploadError && (
                  <p className="mt-1 text-xs text-red-500">{uploadError}</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_due_date')}</label>
                  <input
                    type="datetime-local"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_due_odometer')}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="135000"
                    value={dueOdometerKm}
                    onChange={(e) => setDueOdometerKm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">{t('maintenance.field_priority')}</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as typeof priority)}
                    className="select-dark"
                  >
                    <option value="LOW">{t('maintenance.priority_low')}</option>
                    <option value="MEDIUM">{t('maintenance.priority_medium')}</option>
                    <option value="HIGH">{t('maintenance.priority_high')}</option>
                    <option value="URGENT">{t('maintenance.priority_urgent')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    <Repeat className="h-4 w-4 inline mr-1" />
                    {t('maintenance.field_recurrence')}
                  </label>
                  <select
                    value={recurrence}
                    onChange={(e) => setRecurrence(e.target.value as RecurrenceType | '')}
                    className="select-dark"
                  >
                    <option value="">{t('maintenance.recurrence_none')}</option>
                    <option value="MONTHLY">{t('maintenance.recurrence_monthly_long')}</option>
                    <option value="QUARTERLY">{t('maintenance.recurrence_quarterly_long')}</option>
                    <option value="SEMI_ANNUAL">{t('maintenance.recurrence_semi_annual_long')}</option>
                    <option value="ANNUAL">{t('maintenance.recurrence_annual_long')}</option>
                    <option value="TWO_YEARS">{t('maintenance.recurrence_2y_long')}</option>
                    <option value="FOUR_YEARS">{t('maintenance.recurrence_4y_long')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  <Users className="h-4 w-4 inline mr-1" />
                  {t('maintenance.field_notify_users')}
                </label>
                <div className="relative" ref={userDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-left focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                  >
                    {selectedUserIds.length === 0
                      ? <span className="text-gray-400">{t('maintenance.no_recipient')}</span>
                      : <span>{t('maintenance.users_selected', { count: selectedUserIds.length })}</span>
                    }
                  </button>
                  {showUserDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {tenantUsers.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-400">{t('maintenance.no_users')}</div>
                      ) : (
                        tenantUsers.map((user) => {
                          const isSelected = selectedUserIds.includes(user.id);
                          return (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                setSelectedUserIds((prev) =>
                                  isSelected
                                    ? prev.filter((id) => id !== user.id)
                                    : [...prev, user.id]
                                );
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-primary-50 flex items-center gap-2 transition-colors"
                            >
                              <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <span className="text-gray-900">{user.firstName} {user.lastName}</span>
                              <span className="text-gray-400 text-xs ml-auto">{user.email}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                {selectedUserIds.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {selectedUserIds.map((uid) => {
                      const user = tenantUsers.find((u) => u.id === uid);
                      if (!user) return null;
                      return (
                        <span
                          key={uid}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs"
                        >
                          {user.firstName} {user.lastName}
                          <button
                            type="button"
                            onClick={() => setSelectedUserIds((prev) => prev.filter((id) => id !== uid))}
                            className="hover:text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              {mode === 'record' ? t('maintenance.field_description') : t('maintenance.field_notes')}
            </label>
            <textarea
              rows={2}
              placeholder={t('maintenance.description_placeholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              {t('maintenance.btn_cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-glow flex-1 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  {isEditing ? t('maintenance.btn_save') : (mode === 'record' ? t('maintenance.btn_save') : t('maintenance.btn_schedule'))}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
